// src/components/Layout.js
// Creative MUI shell with mini drawer, breadcrumbs, search, notifications, theme toggle, and logout
// Fully responsive: temporary drawer on mobile/tablet; collapsible mini drawer on desktop

import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { NavLink, useLocation, useNavigate } from 'react-router-dom';
import {
  AppBar,
  Avatar,
  Box,
  Breadcrumbs,
  Chip,
  CssBaseline,
  Divider,
  Drawer,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
  TextField,
} from '@mui/material';

// Icons
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import DashboardIcon from '@mui/icons-material/Dashboard';
import StorefrontIcon from '@mui/icons-material/Storefront';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import EventAvailableIcon from '@mui/icons-material/EventAvailable';
import PointOfSaleIcon from '@mui/icons-material/PointOfSale';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import PaidIcon from '@mui/icons-material/Paid';
import LogoutIcon from '@mui/icons-material/Logout';
import NotificationsActiveIcon from '@mui/icons-material/NotificationsActive';
import SearchIcon from '@mui/icons-material/Search';
import LightModeIcon from '@mui/icons-material/LightMode';
import DarkModeIcon from '@mui/icons-material/DarkMode';

import Footer from './Footer';
import { APP_TITLE, BRAND_INITIALS } from '../config/branding';

const DRAWER_W_OPEN = 240;
const DRAWER_W_MINI = 72;

const groups = [
  {
    title: 'Overview',
    items: [ { to: '/dashboard', label: 'Dashboard', icon: <DashboardIcon/> } ],
  },
  {
    title: 'Operations',
    items: [
      { to: '/att', label: 'Attendance', icon: <EventAvailableIcon/> },
      { to: '/bill', label: 'Bill Book', icon: <PaidIcon/> },
      { to: '/sales', label: 'Credit Sales', icon: <PointOfSaleIcon/> },
      { to: '/purchese', label: 'Purchase Items', icon: <PointOfSaleIcon/> },
      { to: '/purchase-stock', label: 'Purchase Stocks', icon: <PointOfSaleIcon/> },
      { to: '/customers', label: 'Customers', icon: <StorefrontIcon/> },
    ],
  },
  {
    title: 'People',
    items: [
      { to: '/sal', label: 'Salary', icon: <PaidIcon/> },
      { to: '/bonus', label: 'Bonus & Penalties', icon: <PaidIcon/> },
      { to: '/staff', label: 'Staff', icon: <SupervisorAccountIcon/> },
      { to: '/loan', label: 'Loans', icon: <AccountBalanceWalletIcon/> },
      { to: '/resi', label: 'Resignation', icon: <AccountBalanceWalletIcon/> },
    ],
  },
];
const allNavItems = groups.flatMap((g) => g.items);

function BrandMark(){
  const theme = useTheme();
  return (
    <Box sx={{ display:'flex', alignItems:'center', gap:1, px:1 }}>
      <Avatar sx={{ width:32, height:32, fontWeight:800, bgcolor: theme.palette.primary.main }}>{BRAND_INITIALS}</Avatar>
      <Typography variant="subtitle1" fontWeight={800} noWrap>
        {APP_TITLE}
      </Typography>
    </Box>
  );
}

function BreadcrumbsFromPath(){
  const location = useLocation();
  const parts = location.pathname.split('/').filter(Boolean);
  const crumbs = useMemo(()=>{
    const acc = []; let path = '';
    parts.forEach((p, i) => {
      path += '/' + p;
      acc.push({ name: p.replace(/[-_]/g,' ').replace(/\b\w/g, c=>c.toUpperCase()), to: path, last: i===parts.length-1 });
    });
    return acc;
  }, [parts]);

  if (crumbs.length === 0) {
    return (
      <Breadcrumbs aria-label="breadcrumbs">
        <Typography color="text.secondary">Home</Typography>
      </Breadcrumbs>
    );
  }
  return (
    <Breadcrumbs aria-label="breadcrumbs">
      <NavLink to="/dashboard">Home</NavLink>
      {crumbs.map(c => c.last ? (
        <Typography key={c.to} color="text.primary">{c.name}</Typography>
      ) : (
        <NavLink key={c.to} to={c.to}>{c.name}</NavLink>
      ))}
    </Breadcrumbs>
  );
}

const Layout = ({ children, onToggleColorMode }) => {
  const theme = useTheme();
  const location = useLocation();
  const isMobileOrTablet = useMediaQuery(theme.breakpoints.down('md'));
  const isDesktop = !isMobileOrTablet;

  const [mobileOpen, setMobileOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(() => {
    const v = localStorage.getItem('drawer_open');
    return v === null ? true : v === '1';
  });

  useEffect(()=>{ localStorage.setItem('drawer_open', drawerOpen ? '1' : '0'); }, [drawerOpen]);
  useEffect(() => {
    const hit = allNavItems.find((item) => (
      location.pathname === item.to || location.pathname.startsWith(`${item.to}/`)
    ));
    document.title = `${hit?.label || 'Dashboard'} | ${APP_TITLE}`;
  }, [location.pathname]);

  const navigate = useNavigate();
  const handleLogout = useCallback(() => {
    localStorage.removeItem('authToken');
    navigate('/login', { replace: true });
  }, [navigate]);

  const handleDrawerToggle = () => setMobileOpen(o => !o);
  const toggleDesktopDrawer = () => setDrawerOpen(o => !o);

  const drawerWidth = isDesktop ? (drawerOpen ? DRAWER_W_OPEN : DRAWER_W_MINI) : 0;

  const drawerPaperSx = {
    boxSizing: 'border-box',
    width: drawerOpen ? DRAWER_W_OPEN : DRAWER_W_MINI,
    overflowX: 'hidden',
    transition: theme.transitions.create('width', { duration: theme.transitions.duration.standard }),
    borderRight: `1px solid ${theme.palette.divider}`,
  };

  const NavItem = ({ to, label, icon }) => (
    <ListItem disablePadding sx={{ display:'block' }}>
      <ListItemButton
        component={NavLink}
        to={to}
        end
        sx={{
          minHeight: 44,
          px: 1.5,
          justifyContent: drawerOpen ? 'initial' : 'center',
          '&.active': {
            bgcolor: 'action.selected',
            color: 'primary.main',
            '& .MuiListItemIcon-root': { color: 'primary.main' },
          },
          borderRadius: 1.5,
          mx: 0.75,
          my: 0.25,
        }}
      >
        <Tooltip title={drawerOpen ? '' : label} placement="right">
          <ListItemIcon sx={{ minWidth: 36 }}>{icon}</ListItemIcon>
        </Tooltip>
        {drawerOpen && <ListItemText primary={label} />}
      </ListItemButton>
    </ListItem>
  );

  const drawerContent = (
    <Box sx={{ height:'100%', display:'flex', flexDirection:'column' }}>
      <Toolbar sx={{ minHeight: 64, px: 1, justifyContent: drawerOpen ? 'space-between' : 'center' }}>
        {drawerOpen ? <BrandMark/> : <Avatar sx={{ width:32, height:32, fontWeight:800 }}>{BRAND_INITIALS}</Avatar>}
        {isDesktop && (
          <IconButton onClick={toggleDesktopDrawer} size="small">
            {drawerOpen ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
          </IconButton>
        )}
      </Toolbar>
      <Divider />
      <Box sx={{ flex:1, overflowY:'auto' }}>
        {groups.map((g, gi) => (
          <Box key={gi} sx={{ px: 1, py: 1 }}>
            {drawerOpen && (
              <Typography variant="caption" sx={{ px: 2, color:'text.secondary', fontWeight:700 }}>{g.title}</Typography>
            )}
            <List sx={{ py: 0.5 }}>
              {g.items.map(item => (
                <NavItem key={item.to} to={item.to} label={item.label} icon={item.icon} />
              ))}
            </List>
          </Box>
        ))}
      </Box>
      <Divider />
      {/* Footer area inside drawer */}
      <Box sx={{ p:1.25 }}>
        {drawerOpen ? (
          <Chip size="small" label={process.env.REACT_APP_VERSION || 'v1.0'} variant="outlined" />
        ) : (
          <Tooltip title={process.env.REACT_APP_VERSION || 'v1.0'} placement="right"><Chip size="small" label={process.env.REACT_APP_VERSION || 'v1.0'} variant="outlined" /></Tooltip>
        )}
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display:'flex' }}>
      <CssBaseline />

      {/* AppBar */}
      <AppBar position="fixed" sx={{backgroundColor: theme.palette.background.paper, color: 'text.primary', borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Toolbar sx={{ gap: 1 }}>
          {isMobileOrTablet ? (
            <IconButton color="inherit" edge="start" onClick={handleDrawerToggle} sx={{ mr: 1 }}>
              <MenuIcon />
            </IconButton>
          ) : (
            <IconButton color="inherit" edge="start" onClick={toggleDesktopDrawer} sx={{ mr: 1 }}>
              {drawerOpen ? <ChevronLeftIcon/> : <ChevronRightIcon/>}
            </IconButton>
          )}

          {/* Brand on small screens */}
          {/* <Box sx={{ display:{ xs:'flex', md:'none' }, alignItems:'center', mr:1 }}>
            <Typography variant="subtitle2" fontWeight={800}>{APP_TITLE}</Typography>
          </Box> */}

          {/* Breadcrumbs */}
          <Box sx={{ display:'flex', alignItems:'center', flex: 1, minWidth:0 }}>
            <BreadcrumbsFromPath />
          </Box>

          {/* Search */}
          <Box sx={{ display:{ xs:'none', sm:'block' }, minWidth: 260 }}>
            <TextField
              size="small"
              placeholder="Search…"
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchIcon/></InputAdornment> }}
            />
          </Box>

          {/* Actions */}
          <Tooltip title="Notifications">
            <IconButton><NotificationsActiveIcon/></IconButton>
          </Tooltip>
          <Tooltip title={theme.palette.mode === 'dark' ? 'Light mode' : 'Dark mode'}>
            <span>
              <IconButton onClick={onToggleColorMode}>
                {theme.palette.mode === 'dark' ? <LightModeIcon/> : <DarkModeIcon/>}
              </IconButton>
            </span>
          </Tooltip>
          <Tooltip title="Logout">
            <IconButton color="error" onClick={handleLogout}><LogoutIcon/></IconButton>
          </Tooltip>
        </Toolbar>
      </AppBar>

      {/* Drawer */}
      {isMobileOrTablet ? (
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{ display: { xs: 'block', md: 'block', lg: 'none' }, '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {drawerContent}
        </Drawer>
      ) : (
        <Drawer
          variant="permanent"
          open={drawerOpen}
          sx={{ display: { xs: 'none', lg: 'block' }, '& .MuiDrawer-paper': drawerPaperSx }}
        >
          {drawerContent}
        </Drawer>
      )}

      {/* Main content */}
      <Box component="main" sx={{
        flexGrow: 1,
        ml: { lg: `${drawerWidth}px` },
        transition: theme.transitions.create('margin', { duration: theme.transitions.duration.standard }),
        p: 3,
        backgroundColor: theme.palette.mode==='light' ? '#f5f7fb' : 'background.default',
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}>
        <Toolbar />
        {children}
        <Footer />
      </Box>
    </Box>
  );
};

export default Layout;
